import { canvasSteps } from '@bb/contracts';

/**
 * Context step settings as the editor reads and writes them (BB-119): `canvasSteps.ContextStepConfig` of
 * `@bb/contracts`, the schema the runtime handler and the draft validation use. The panel shows its rules per field;
 * the contract stays the final word on the server.
 */
export type ContextVariableType = (typeof canvasSteps.CONTEXT_VARIABLE_TYPES)[number];

export interface ContextVariable {
  name: string;
  type: ContextVariableType;
  /** Liquid, rendered when the user reaches the step. */
  expression: string;
}

export interface ContextConfig {
  variables: ContextVariable[];
}

export const CONTEXT_MAX_VARIABLES = canvasSteps.CONTEXT_MAX_VARIABLES;
/** `expression: z.string().min(1).max(10_000)` in the contract. */
export const CONTEXT_EXPRESSION_MAX = 10_000;
const NAME_MAX = 100;

/** How each type is read from the rendered text (`coerceContextValue` of the handler); every contract type has one. */
export const CONTEXT_TYPE_INFO: Readonly<Record<ContextVariableType, { label: string; hint: string }>> = {
  boolean: { label: 'Boolean', hint: 'true or false, in any letter case.' },
  number: { label: 'Number', hint: 'A finite number, e.g. 100 or 12.5.' },
  string: { label: 'String', hint: 'The rendered text as it is.' },
  array: { label: 'Array', hint: 'A JSON array, e.g. ["EURUSD", "GBPUSD"].' },
  time: { label: 'Time', hint: 'An ISO date or time, or Unix seconds or milliseconds; kept as ISO in UTC.' },
  object: { label: 'Object', hint: 'A JSON object, e.g. {"symbol": "EURUSD"}.' },
};

export const CONTEXT_TYPE_OPTIONS = canvasSteps.CONTEXT_VARIABLE_TYPES.map((value) => ({ value, label: CONTEXT_TYPE_INFO[value].label }));

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isType = (v: unknown): v is ContextVariableType => (canvasSteps.CONTEXT_VARIABLE_TYPES as readonly unknown[]).includes(v);

/** The stored settings with unknown keys left out; an unknown type reads as a string. */
export function readContext(raw: unknown): ContextConfig {
  const c = isObject(raw) ? raw : {};
  const variables = (Array.isArray(c.variables) ? c.variables : []).filter(isObject).map(
    (v): ContextVariable => ({
      name: typeof v.name === 'string' ? v.name : '',
      type: isType(v.type) ? v.type : 'string',
      expression: typeof v.expression === 'string' ? v.expression : '',
    }),
  );
  return { variables };
}

/** A variable name not used yet: `variable_1`, `variable_2`, … */
export function nextVariableName(variables: readonly ContextVariable[]): string {
  for (let n = variables.length + 1; ; n++) if (!variables.some((v) => v.name === `variable_${n}`)) return `variable_${n}`;
}

export const newVariable = (variables: readonly ContextVariable[]): ContextVariable => ({ name: nextVariableName(variables), type: 'string', expression: '' });

/** How a later step reads a variable. */
export const contextReference = (name: string) => `{{context.\${${name}}}}`;

export interface ContextIssues {
  /** About the list itself: none, or more than the limit. */
  variables?: string;
  /** Per variable, by position. */
  each: Array<{ name?: string; expression?: string }>;
}

/** The contract's rules as messages per field. */
export function contextIssues(c: ContextConfig): ContextIssues {
  const out: ContextIssues = { each: [] };
  if (c.variables.length === 0) out.variables = 'Add at least one variable.';
  else if (c.variables.length > CONTEXT_MAX_VARIABLES) out.variables = `At most ${CONTEXT_MAX_VARIABLES} variables.`;
  out.each = c.variables.map((v) => {
    const own: ContextIssues['each'][number] = {};
    if (!v.name) own.name = 'Enter a name.';
    else if (v.name.length > NAME_MAX) own.name = `At most ${NAME_MAX} characters.`;
    else if (!canvasSteps.ContextVariableName.safeParse(v.name).success) own.name = 'Use letters, digits and _ only.';
    else if (c.variables.filter((x) => x.name === v.name).length > 1) own.name = `Two variables are named “${v.name}”: names must be unique.`;
    if (!v.expression) own.expression = 'Enter a Liquid expression.';
    else if (v.expression.length > CONTEXT_EXPRESSION_MAX) own.expression = `At most ${CONTEXT_EXPRESSION_MAX.toLocaleString('en-US')} characters.`;
    return own;
  });
  return out;
}

export const hasContextIssues = (i: ContextIssues) => Boolean(i.variables) || i.each.some((v) => v.name || v.expression);

/** A context variable that earlier Context steps set. */
export interface EarlierContextVariable {
  name: string;
  /** Every type those steps give it, each once (usually one). */
  types: ContextVariableType[];
  /** Every earlier Context step that sets it (on some path to the step). */
  stepIds: string[];
}

interface GraphStep {
  type: string;
  config: unknown;
  next: ReadonlyArray<{ stepId: string | null }>;
}

/**
 * The context variables a step can read from earlier Context steps: those on some path from a variant start to it,
 * never the step itself or steps after it. The same walk as `contextVariablesBefore` in `@bb/domain`
 * (`journeys/context-catalog.ts`, which the dashboard does not depend on); where several steps set a name, it lists
 * all of them and their types instead of the last one found.
 */
export function contextVariablesBefore(steps: Readonly<Record<string, GraphStep>>, stepId: string): EarlierContextVariable[] {
  const parents = new Map<string, string[]>();
  for (const [id, step] of Object.entries(steps)) for (const n of step.next) if (n.stepId) parents.set(n.stepId, [...(parents.get(n.stepId) ?? []), id]);
  const ancestors = new Set<string>();
  const stack = [...(parents.get(stepId) ?? [])];
  while (stack.length) {
    const id = stack.pop()!;
    if (ancestors.has(id)) continue;
    ancestors.add(id);
    stack.push(...(parents.get(id) ?? []));
  }
  const out = new Map<string, EarlierContextVariable>();
  for (const id of ancestors) {
    const step = steps[id];
    if (step?.type !== 'context' || id === stepId) continue;
    const config = canvasSteps.ContextStepConfig.safeParse(step.config);
    if (!config.success) continue;
    for (const v of config.data.variables) {
      const known = out.get(v.name) ?? { name: v.name, types: [], stepIds: [] };
      out.set(v.name, { name: v.name, types: known.types.includes(v.type) ? known.types : [...known.types, v.type], stepIds: [...known.stepIds, id] });
    }
  }
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
}
